import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const RINGS = [
  { label: "Recommended starting allocation", value: "$35k+", detail: "Initial brokerage capital for the diversified suite", pct: 72, color: "#818cf8" },
  { label: "White-glove configuration", value: "Sized to you", detail: "Position sizing, VPS, and strategy mix set with our team", pct: 48, color: "#06b6d4" },
  { label: "Your custody", value: "100%", detail: "Funds stay in your regulated futures brokerage account", pct: 100, color: "#10b981" },
];

function useRingVisible(ref) {
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (inView) setVisible(true);
  }, [inView]);

  // iOS Safari can miss IntersectionObserver under overflow-hidden ancestors.
  // Fall back to a simple viewport check on scroll/resize.
  useEffect(() => {
    if (visible) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const check = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < vh * 0.92 && rect.bottom > vh * 0.08) {
        setVisible(true);
      }
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    const t = window.setTimeout(check, 400);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      window.clearTimeout(t);
    };
  }, [ref, visible]);

  return visible;
}

function Ring({ pct, color, delay }) {
  const ref = useRef(null);
  const visible = useRingVisible(ref);
  const r = 42;
  const c = 2 * Math.PI * r;
  const filledOffset = c * (1 - pct / 100);

  return (
    <svg ref={ref} viewBox="0 0 100 100" className="w-24 h-24 md:w-28 md:h-28">
      <circle
        cx="50"
        cy="50"
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth="8"
      />
      <circle
        cx="50"
        cy="50"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={visible ? filledOffset : c}
        transform="rotate(-90 50 50)"
        style={{
          transition: `stroke-dashoffset 1.1s ease-out ${delay}s`,
        }}
      />
    </svg>
  );
}

/**
 * Simple capital-story graphic: recommended allocation, configuration, custody.
 */
export default function CapitalAllocationVisual() {
  return (
    <div className="grid md:grid-cols-3 gap-5">
      {RINGS.map((item, i) => (
        <motion.div
          key={item.label}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.45, delay: i * 0.07 }}
          className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6 flex flex-col items-center text-center"
        >
          <Ring pct={item.pct} color={item.color} delay={0.12 + i * 0.08} />
          <p className="mt-4 font-display text-3xl text-white tracking-tight">{item.value}</p>
          <p className="mt-2 text-sm font-semibold text-zinc-200">{item.label}</p>
          <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{item.detail}</p>
        </motion.div>
      ))}
    </div>
  );
}
